// Glossary of every financial concept the site computes/uses.
// Each entry powers /learn/{slug} with SEO meta, JSON-LD, and CTA back to /analyze.
//
// Structure per concept:
//   slug:        URL slug (kebab-case)
//   title:       H1 + Google title
//   category:    Profitability | Liquidity | Leverage | Efficiency | Industry-Specific | Methodology
//   short:       one-sentence definition (used for meta description and hub card)
//   formula:     calculation formula (math notation)
//   formulaPlain: same formula in plain English
//   why:         why it matters (paragraph)
//   benchmarks:  array of { label, value, tone: 'good' | 'warn' | 'bad' | 'neutral' }
//   example:     optional worked example object { inputs: [{k,v}], result: string }
//   related:     array of slugs
//   industryKey: optional — links to backend benchmark key for the analyze pre-fill

export const LEARN_CATEGORIES = [
  { key: "Profitability", label: "Profitability" },
  { key: "Liquidity", label: "Liquidity" },
  { key: "Leverage", label: "Leverage & Debt" },
  { key: "Efficiency", label: "Efficiency" },
  { key: "Industry-Specific", label: "Industry-Specific" },
  { key: "Methodology", label: "Methodology" },
];

export const LEARN_CONCEPTS = [
  // ---------- PROFITABILITY ----------
  {
    slug: "gross-margin",
    title: "Gross Margin",
    category: "Profitability",
    short:
      "Gross margin is the percentage of revenue left after the direct cost of producing your product or service.",
    formula: "Gross Margin (%) = (Revenue − COGS) / Revenue × 100",
    formulaPlain:
      "Take your revenue, subtract the direct cost of what you sold (COGS), divide by revenue, multiply by 100.",
    why:
      "Gross margin is the ceiling on every other margin. If you can't make money on the unit, scaling won't fix it — you'll just lose money faster. Lenders and acquirers use it to gauge pricing power and operational efficiency.",
    benchmarks: [
      { label: "SaaS / Software", value: "70–85%", tone: "good" },
      { label: "Professional services", value: "40–60%", tone: "good" },
      { label: "Retail / e-commerce", value: "30–50%", tone: "neutral" },
      { label: "Restaurants", value: "60–72% (food margin)", tone: "neutral" },
      { label: "Wholesale / distribution", value: "15–25%", tone: "neutral" },
      { label: "Construction", value: "15–25%", tone: "neutral" },
    ],
    example: {
      inputs: [
        { k: "Revenue", v: "$1,000,000" },
        { k: "COGS", v: "$620,000" },
      ],
      result: "(1,000,000 − 620,000) / 1,000,000 × 100 = 38% gross margin",
    },
    related: ["ebitda-margin", "net-margin", "operating-margin"],
  },
  {
    slug: "operating-margin",
    title: "Operating Margin",
    category: "Profitability",
    short:
      "Operating margin is the percentage of revenue left after both direct costs (COGS) and operating expenses like rent, salaries, and overhead.",
    formula: "Operating Margin (%) = Operating Income / Revenue × 100",
    formulaPlain:
      "Take revenue, subtract COGS and operating expenses, divide the result by revenue, multiply by 100.",
    why:
      "Operating margin shows whether the day-to-day business is profitable before interest and taxes muddy the picture. Two companies with identical gross margins can have wildly different operating margins depending on how lean their overhead is.",
    benchmarks: [
      { label: "Strong / scalable", value: "≥ 15%", tone: "good" },
      { label: "Healthy", value: "8–15%", tone: "good" },
      { label: "Watch closely", value: "3–8%", tone: "warn" },
      { label: "At-risk", value: "< 3%", tone: "bad" },
    ],
    related: ["gross-margin", "ebitda-margin", "net-margin"],
  },
  {
    slug: "ebitda",
    title: "EBITDA",
    category: "Profitability",
    short:
      "EBITDA stands for Earnings Before Interest, Taxes, Depreciation and Amortization — a measure of operational cash earnings.",
    formula:
      "EBITDA = Net Income + Interest + Taxes + Depreciation + Amortization",
    formulaPlain:
      "Start with net income, then add back interest expense, taxes, depreciation, and amortization. The result approximates operating cash earnings.",
    why:
      "EBITDA is the standard yardstick lenders and acquirers use to compare businesses regardless of how they're financed, taxed, or how they depreciate assets. SBA lenders, sureties, and most acquirers anchor their math on EBITDA.",
    benchmarks: [
      { label: "Used in DSCR", value: "DSCR = EBITDA / debt service", tone: "neutral" },
      { label: "Used in Debt / EBITDA", value: "leverage multiple", tone: "neutral" },
      { label: "Used in valuation", value: "EV / EBITDA multiples", tone: "neutral" },
    ],
    related: ["ebitda-margin", "dscr", "debt-to-ebitda"],
  },
  {
    slug: "ebitda-margin",
    title: "EBITDA Margin",
    category: "Profitability",
    short:
      "EBITDA margin is EBITDA as a percentage of revenue — the most common shorthand for how profitable a business is at an operational level.",
    formula: "EBITDA Margin (%) = EBITDA / Revenue × 100",
    formulaPlain:
      "Take EBITDA (operating cash earnings), divide by revenue, multiply by 100.",
    why:
      "EBITDA margin lets you compare businesses across sizes and industries. A 5% EBITDA margin on a grocery store is healthy; a 5% EBITDA margin on a SaaS company is alarming. CFO Grade scores it against industry-specific medians.",
    benchmarks: [
      { label: "Excellent (most industries)", value: "≥ 20%", tone: "good" },
      { label: "Healthy", value: "10–20%", tone: "good" },
      { label: "Marginal", value: "5–10%", tone: "warn" },
      { label: "Distress", value: "< 5%", tone: "bad" },
    ],
    example: {
      inputs: [
        { k: "Revenue", v: "$2,000,000" },
        { k: "EBITDA", v: "$240,000" },
      ],
      result: "240,000 / 2,000,000 × 100 = 12% EBITDA margin",
    },
    related: ["ebitda", "gross-margin", "net-margin"],
  },
  {
    slug: "net-margin",
    title: "Net Margin",
    category: "Profitability",
    short:
      "Net margin is the percentage of revenue you actually keep after every expense — including taxes and interest.",
    formula: "Net Margin (%) = Net Income / Revenue × 100",
    formulaPlain:
      "Take net income (the bottom line of your income statement), divide by revenue, multiply by 100.",
    why:
      "Net margin is the true bottom line. It's what's left for owners, reinvestment, or paying down debt. Two businesses with identical EBITDA can have very different net margins if one is debt-heavy or tax-inefficient.",
    benchmarks: [
      { label: "Strong", value: "≥ 15%", tone: "good" },
      { label: "Healthy", value: "7–15%", tone: "good" },
      { label: "Thin", value: "3–7%", tone: "warn" },
      { label: "Distress", value: "< 3%", tone: "bad" },
    ],
    related: ["ebitda-margin", "operating-margin", "gross-margin"],
  },

  // ---------- LIQUIDITY ----------
  {
    slug: "current-ratio",
    title: "Current Ratio",
    category: "Liquidity",
    short:
      "Current ratio measures whether you have enough short-term assets to cover your short-term obligations.",
    formula: "Current Ratio = Current Assets / Current Liabilities",
    formulaPlain:
      "Add up cash, receivables, inventory, and other current assets. Divide by the sum of accounts payable, short-term debt, and other current liabilities.",
    why:
      "A current ratio below 1.0× means your business technically can't cover its next 12 months of bills with its next 12 months of assets. Lenders and credit committees flag anything below 1.2× as a risk.",
    benchmarks: [
      { label: "Strong", value: "≥ 2.0×", tone: "good" },
      { label: "Healthy", value: "1.5–2.0×", tone: "good" },
      { label: "Adequate", value: "1.2–1.5×", tone: "warn" },
      { label: "Risk", value: "< 1.0×", tone: "bad" },
    ],
    related: ["quick-ratio", "working-capital", "cash-conversion-cycle"],
  },
  {
    slug: "quick-ratio",
    title: "Quick Ratio (Acid-Test Ratio)",
    category: "Liquidity",
    short:
      "Quick ratio is the stricter sibling of the current ratio — it excludes inventory, which isn't always easy to convert to cash.",
    formula: "Quick Ratio = (Current Assets − Inventory) / Current Liabilities",
    formulaPlain:
      "Take current assets, subtract inventory, then divide by current liabilities. This shows whether you can pay short-term bills without selling product.",
    why:
      "For inventory-heavy businesses (retail, e-commerce, manufacturing) the current ratio can look fine while the business is actually cash-poor. Quick ratio strips that out and shows the truth.",
    benchmarks: [
      { label: "Strong", value: "≥ 1.5×", tone: "good" },
      { label: "Healthy", value: "1.0–1.5×", tone: "good" },
      { label: "Tight", value: "0.5–1.0×", tone: "warn" },
      { label: "Risk", value: "< 0.5×", tone: "bad" },
    ],
    related: ["current-ratio", "working-capital", "cash-conversion-cycle"],
  },
  {
    slug: "working-capital",
    title: "Working Capital",
    category: "Liquidity",
    short:
      "Working capital is the dollar amount of current assets minus current liabilities — the cash cushion your operations have.",
    formula: "Working Capital = Current Assets − Current Liabilities",
    formulaPlain:
      "Add up all current assets (cash + receivables + inventory + other). Subtract all current liabilities (payables + short-term debt + other). The result is your working capital.",
    why:
      "Negative working capital means you owe more in the next 12 months than you'll collect. Some industries (subscription, restaurants) operate on negative working capital intentionally because they collect cash before paying suppliers. For most businesses, negative working capital is a warning.",
    benchmarks: [
      { label: "Positive & growing", value: "Healthy", tone: "good" },
      { label: "Positive but flat", value: "Watch", tone: "warn" },
      { label: "Negative", value: "Risk (unless intentional)", tone: "bad" },
    ],
    related: ["current-ratio", "quick-ratio", "cash-conversion-cycle"],
  },
  {
    slug: "cash-conversion-cycle",
    title: "Cash Conversion Cycle",
    category: "Liquidity",
    short:
      "Cash conversion cycle is the number of days between paying for inventory and getting paid by customers.",
    formula: "CCC = DSO + DIO − DPO",
    formulaPlain:
      "Days Sales Outstanding (how long customers take to pay you) + Days Inventory Outstanding (how long inventory sits before selling) − Days Payable Outstanding (how long you take to pay suppliers).",
    why:
      "CCC is the most under-appreciated cash metric. A growing business with a long CCC will need ever-more financing just to keep up. Reducing CCC by 10 days frees up real cash — sometimes hundreds of thousands of dollars.",
    benchmarks: [
      { label: "Excellent", value: "< 30 days", tone: "good" },
      { label: "Healthy", value: "30–60 days", tone: "good" },
      { label: "Long", value: "60–90 days", tone: "warn" },
      { label: "Risk", value: "> 120 days", tone: "bad" },
    ],
    related: ["dso", "dio", "dpo", "working-capital"],
  },
  {
    slug: "dso",
    title: "Days Sales Outstanding (DSO)",
    category: "Efficiency",
    short:
      "DSO is the average number of days it takes for customers to pay you after you send an invoice.",
    formula: "DSO = (Accounts Receivable / Revenue) × 365",
    formulaPlain:
      "Take your accounts receivable, divide by annual revenue, multiply by 365.",
    why:
      "Long DSO means cash is tied up in customer invoices. Each extra day of DSO is real money you're financing for free. Companies with strong DSO discipline win on cash flow even with the same revenue.",
    benchmarks: [
      { label: "Excellent (cash businesses)", value: "< 15 days", tone: "good" },
      { label: "Healthy", value: "30–45 days", tone: "good" },
      { label: "Slow", value: "45–60 days", tone: "warn" },
      { label: "Collection problem", value: "> 75 days", tone: "bad" },
    ],
    related: ["cash-conversion-cycle", "dio", "dpo", "receivables-turnover"],
  },
  {
    slug: "dio",
    title: "Days Inventory Outstanding (DIO)",
    category: "Efficiency",
    short:
      "DIO is the average number of days inventory sits on your shelves before being sold.",
    formula: "DIO = (Inventory / COGS) × 365",
    formulaPlain:
      "Take inventory on hand, divide by annual cost of goods sold, multiply by 365.",
    why:
      "Inventory is dead cash. High DIO means you're carrying product that's not turning. It also creates write-down risk if anything goes stale, out-of-fashion, or obsolete.",
    benchmarks: [
      { label: "Fast turn (DTC, food)", value: "< 30 days", tone: "good" },
      { label: "Healthy retail", value: "30–60 days", tone: "good" },
      { label: "Slow", value: "60–120 days", tone: "warn" },
      { label: "Inventory problem", value: "> 180 days", tone: "bad" },
    ],
    related: ["cash-conversion-cycle", "dso", "dpo", "inventory-turnover"],
  },
  {
    slug: "dpo",
    title: "Days Payable Outstanding (DPO)",
    category: "Efficiency",
    short:
      "DPO is the average number of days you take to pay your suppliers.",
    formula: "DPO = (Accounts Payable / COGS) × 365",
    formulaPlain:
      "Take accounts payable, divide by annual cost of goods sold, multiply by 365.",
    why:
      "DPO is the only part of the cash conversion cycle you can stretch in your favor. Healthy DPO means using supplier credit terms strategically. Too-high DPO damages relationships and credit ratings.",
    benchmarks: [
      { label: "Negotiation strength", value: "45–60 days", tone: "good" },
      { label: "Standard terms", value: "30–45 days", tone: "good" },
      { label: "Cash too tight to pay", value: "> 75 days", tone: "bad" },
    ],
    related: ["cash-conversion-cycle", "dso", "dio", "payables-turnover"],
  },

  // ---------- LEVERAGE / DEBT ----------
  {
    slug: "dscr",
    title: "DSCR (Debt Service Coverage Ratio)",
    category: "Leverage",
    short:
      "DSCR measures whether your business generates enough cash to cover its annual debt payments.",
    formula: "DSCR = EBITDA / (Annual Principal + Interest Payments)",
    formulaPlain:
      "Take EBITDA (operating cash earnings) and divide by your total annual debt service — both principal and interest payments combined.",
    why:
      "DSCR is the single most important metric in commercial and SBA lending. A DSCR of 1.0× means your earnings exactly cover your debt — no margin for error. Most SBA lenders require ≥ 1.25×. Construction sureties often require ≥ 1.5×. Below 1.0× and you're net-cash-negative on debt.",
    benchmarks: [
      { label: "Strong (multiple lenders compete)", value: "≥ 1.5×", tone: "good" },
      { label: "SBA minimum", value: "≥ 1.25×", tone: "good" },
      { label: "Tight — fix before applying", value: "1.0–1.25×", tone: "warn" },
      { label: "Below break-even", value: "< 1.0×", tone: "bad" },
    ],
    example: {
      inputs: [
        { k: "EBITDA", v: "$300,000" },
        { k: "Annual debt service", v: "$200,000" },
      ],
      result: "300,000 / 200,000 = 1.50× DSCR — strong",
    },
    related: ["ebitda", "debt-to-ebitda", "interest-coverage", "debt-to-equity"],
  },
  {
    slug: "debt-to-ebitda",
    title: "Debt to EBITDA",
    category: "Leverage",
    short:
      "Debt to EBITDA shows how many years of earnings it would take to pay off your total debt.",
    formula: "Debt to EBITDA = Total Debt / EBITDA",
    formulaPlain:
      "Add up all interest-bearing debt (short-term + long-term). Divide by EBITDA.",
    why:
      "This is how acquirers, leveraged-buyout investors, and senior lenders measure how levered a business is. Most credit committees won't approve a deal above 4.0×. Above 5.0× is considered junk-rated leverage.",
    benchmarks: [
      { label: "Conservative", value: "< 2.0×", tone: "good" },
      { label: "Healthy", value: "2.0–3.0×", tone: "good" },
      { label: "Elevated", value: "3.0–4.0×", tone: "warn" },
      { label: "High-yield / risky", value: "> 5.0×", tone: "bad" },
    ],
    related: ["dscr", "debt-to-equity", "ebitda", "interest-coverage"],
  },
  {
    slug: "debt-to-equity",
    title: "Debt to Equity Ratio",
    category: "Leverage",
    short:
      "Debt to equity shows how much of your business is financed by lenders versus owners.",
    formula: "Debt to Equity = Total Debt / Total Equity",
    formulaPlain:
      "Total interest-bearing debt divided by total owner equity (retained earnings + paid-in capital).",
    why:
      "A high D/E ratio means most of the business is funded by lenders — magnifying both returns and risk. Capital-intensive industries (utilities, real estate) tolerate higher D/E; asset-light industries (SaaS, services) should run lean.",
    benchmarks: [
      { label: "Conservative", value: "< 0.5×", tone: "good" },
      { label: "Healthy", value: "0.5–1.0×", tone: "good" },
      { label: "Levered", value: "1.0–2.0×", tone: "warn" },
      { label: "High-risk", value: "> 2.0×", tone: "bad" },
    ],
    related: ["debt-to-ebitda", "debt-to-assets", "dscr"],
  },
  {
    slug: "debt-to-assets",
    title: "Debt to Assets",
    category: "Leverage",
    short:
      "Debt to assets measures what percent of your total assets are funded by debt versus equity.",
    formula: "Debt to Assets = Total Debt / Total Assets",
    formulaPlain:
      "Add up total debt (short-term + long-term). Divide by total assets on the balance sheet.",
    why:
      "Debt to assets is a quick read on solvency. Above 60% means more than half the business is owed to creditors. Capital-intensive businesses (real estate, construction) run higher; service businesses should run lower.",
    benchmarks: [
      { label: "Conservative", value: "< 30%", tone: "good" },
      { label: "Healthy", value: "30–50%", tone: "good" },
      { label: "Levered", value: "50–70%", tone: "warn" },
      { label: "Risk", value: "> 70%", tone: "bad" },
    ],
    related: ["debt-to-equity", "debt-to-ebitda", "dscr"],
  },
  {
    slug: "interest-coverage",
    title: "Interest Coverage Ratio",
    category: "Leverage",
    short:
      "Interest coverage shows how many times over your operating profit can pay your annual interest expense.",
    formula: "Interest Coverage = EBIT / Interest Expense",
    formulaPlain:
      "Take operating income (EBIT) and divide by your annual interest expense.",
    why:
      "Interest coverage isolates the question 'can we afford our debt?' from principal payments. Below 1.5× and a single bad quarter could mean missed interest. Bond covenants often require ≥ 2.0× minimum.",
    benchmarks: [
      { label: "Strong", value: "≥ 4.0×", tone: "good" },
      { label: "Healthy", value: "2.0–4.0×", tone: "good" },
      { label: "Tight", value: "1.5–2.0×", tone: "warn" },
      { label: "Risk", value: "< 1.5×", tone: "bad" },
    ],
    related: ["dscr", "debt-to-ebitda", "ebitda"],
  },

  // ---------- EFFICIENCY ----------
  {
    slug: "inventory-turnover",
    title: "Inventory Turnover",
    category: "Efficiency",
    short:
      "Inventory turnover is how many times per year your inventory cycles through the business.",
    formula: "Inventory Turnover = COGS / Inventory",
    formulaPlain:
      "Cost of goods sold for the year, divided by the value of inventory on hand.",
    why:
      "High inventory turnover means inventory isn't tied up as dead cash. Low turnover risks markdowns, write-offs, and storage costs. The 'right' number is highly industry-dependent.",
    benchmarks: [
      { label: "Fast turn (food, DTC)", value: "≥ 10×/yr", tone: "good" },
      { label: "Healthy retail", value: "4–8×/yr", tone: "good" },
      { label: "Slow", value: "2–4×/yr", tone: "warn" },
      { label: "Stagnant", value: "< 2×/yr", tone: "bad" },
    ],
    related: ["dio", "cash-conversion-cycle", "inventory-shrinkage"],
  },
  {
    slug: "receivables-turnover",
    title: "Receivables Turnover",
    category: "Efficiency",
    short:
      "Receivables turnover is how many times per year you collect your average accounts receivable balance.",
    formula: "Receivables Turnover = Revenue / Accounts Receivable",
    formulaPlain:
      "Annual revenue divided by accounts receivable on the balance sheet.",
    why:
      "Higher turnover = faster collections = stronger cash flow. Falling receivables turnover is one of the earliest warning signs that customer credit quality is deteriorating.",
    benchmarks: [
      { label: "Fast-paying customers", value: "≥ 12×/yr", tone: "good" },
      { label: "Healthy", value: "6–12×/yr", tone: "good" },
      { label: "Slow", value: "4–6×/yr", tone: "warn" },
      { label: "Collection issue", value: "< 4×/yr", tone: "bad" },
    ],
    related: ["dso", "cash-conversion-cycle"],
  },
  {
    slug: "payables-turnover",
    title: "Payables Turnover",
    category: "Efficiency",
    short:
      "Payables turnover is how many times per year you pay off your average accounts payable balance.",
    formula: "Payables Turnover = COGS / Accounts Payable",
    formulaPlain:
      "Annual cost of goods sold divided by accounts payable on the balance sheet.",
    why:
      "Lower turnover (higher DPO) usually means you're using supplier credit strategically. Too low can damage supplier relationships; too high means you're paying earlier than you have to.",
    benchmarks: [
      { label: "Stretching terms strategically", value: "6–8×/yr", tone: "good" },
      { label: "Standard", value: "8–12×/yr", tone: "neutral" },
      { label: "Paying very early", value: "> 12×/yr", tone: "warn" },
    ],
    related: ["dpo", "cash-conversion-cycle"],
  },

  // ---------- INDUSTRY-SPECIFIC ----------
  {
    slug: "food-cost",
    title: "Food Cost Percentage (Restaurants)",
    category: "Industry-Specific",
    short:
      "Food cost is the cost of ingredients as a percentage of food sales — the single most important number in restaurant economics.",
    formula: "Food Cost (%) = Cost of Food Sold / Food Sales × 100",
    formulaPlain:
      "Take the cost of the food you used during the period, divide by food sales (not total revenue), multiply by 100.",
    why:
      "Food cost is the largest controllable expense in a restaurant. Industry-leading restaurants target ~28–32%. Anything above 35% and you're either pricing too low, wasting too much, or stealing from yourself.",
    benchmarks: [
      { label: "Industry-leading", value: "< 28%", tone: "good" },
      { label: "Healthy target", value: "28–32%", tone: "good" },
      { label: "Too high", value: "32–38%", tone: "warn" },
      { label: "Bleeding cash", value: "> 38%", tone: "bad" },
    ],
    industryKey: "restaurant",
    related: ["labor-cost", "prime-cost", "gross-margin"],
  },
  {
    slug: "labor-cost",
    title: "Labor Cost Percentage (Restaurants & Services)",
    category: "Industry-Specific",
    short:
      "Labor cost is total wages and payroll taxes as a percentage of revenue — the second pillar of restaurant economics alongside food cost.",
    formula: "Labor Cost (%) = (Wages + Payroll Taxes + Benefits) / Revenue × 100",
    formulaPlain:
      "Add up all wages, payroll taxes, and benefits for the period. Divide by total revenue. Multiply by 100.",
    why:
      "Labor cost is the second-largest controllable expense after food. Most healthy restaurants run 28–35% labor cost. Combined with food cost, this gives you 'prime cost' — the number that decides whether you're profitable.",
    benchmarks: [
      { label: "Lean operation", value: "< 28%", tone: "good" },
      { label: "Healthy target", value: "28–32%", tone: "good" },
      { label: "Bloated", value: "32–38%", tone: "warn" },
      { label: "Unsustainable", value: "> 38%", tone: "bad" },
    ],
    industryKey: "restaurant",
    related: ["food-cost", "prime-cost", "operating-margin"],
  },
  {
    slug: "prime-cost",
    title: "Prime Cost (Restaurants)",
    category: "Industry-Specific",
    short:
      "Prime cost is food cost plus labor cost combined — the single number that decides whether a restaurant can be profitable.",
    formula: "Prime Cost (%) = (Food Cost + Labor Cost) / Revenue × 100",
    formulaPlain:
      "Add cost of food sold and total labor cost (wages + payroll taxes + benefits). Divide by revenue. Multiply by 100.",
    why:
      "Prime cost is the master KPI in restaurants. Most operators target ≤ 60% prime cost. Above 65% and rent, utilities, marketing, and management have to be funded from less than 35% of revenue — and there's usually nothing left for the owner.",
    benchmarks: [
      { label: "Industry-leading", value: "< 55%", tone: "good" },
      { label: "Healthy", value: "55–60%", tone: "good" },
      { label: "Too high", value: "60–65%", tone: "warn" },
      { label: "Losing money", value: "> 65%", tone: "bad" },
    ],
    industryKey: "restaurant",
    related: ["food-cost", "labor-cost", "ebitda-margin", "occupancy-cost"],
  },
  {
    slug: "occupancy-cost",
    title: "Occupancy Cost (Restaurants & Retail)",
    category: "Industry-Specific",
    short:
      "Occupancy cost is total rent, common-area maintenance (CAM), real-estate taxes, and insurance — expressed as a percentage of sales. It is the largest fixed cost most physical businesses carry, and the ratio that's considered healthy varies sharply by format (grocery vs. mall-based specialty retail vs. urban full-service restaurant are not the same game).",
    formula: "Occupancy Cost (%) = (Rent + CAM + RE Taxes + Insurance) / Revenue × 100",
    formulaPlain:
      "Add every cost of being in the building: base rent, common-area maintenance, property taxes (your share), and property insurance. Divide by revenue for the same period. Multiply by 100.",
    why:
      "Occupancy is a fixed cost — it doesn't fall when sales fall, which is why a 2–3% sales decline can swing a healthy single-location business into a loss. The widely-cited operator rule of thumb is that rent + CAM + utilities combined should stay under roughly one day's worth of weekly sales (i.e. about 14% combined). Pure occupancy (excluding utilities) varies by format: quick-service restaurants and grocery stores typically run lower as a % of sales because they have higher revenue density per square foot; mall-based specialty retail and prime urban full-service restaurants run higher because rent and CAM are structurally more expensive.",
    benchmarks: [
      { label: "Grocery / big-box retail", value: "1–4% of sales", tone: "neutral" },
      { label: "Convenience / drug stores", value: "3–6% of sales", tone: "neutral" },
      { label: "QSR / fast-casual", value: "6–10% of sales", tone: "neutral" },
      { label: "Full-service restaurants", value: "6–10% of sales (12%+ in prime urban)", tone: "neutral" },
      { label: "Strip-center specialty retail", value: "6–10% of sales", tone: "neutral" },
      { label: "Mall-based specialty retail", value: "10–15% of sales (CAM-driven)", tone: "neutral" },
      { label: "Universal red flag (any format)", value: "> 15% of sales", tone: "bad" },
    ],
    industryKey: "restaurant",
    related: ["prime-cost", "food-cost", "labor-cost"],
  },
  {
    slug: "inventory-shrinkage",
    title: "Inventory Shrinkage (Retail)",
    category: "Industry-Specific",
    short:
      "Inventory shrinkage is the gap between book inventory and physical inventory — the value of merchandise lost to theft, fraud, administrative errors, vendor errors, and damage. The National Retail Federation publishes annual shrink reports tracking the industry rate.",
    formula: "Shrinkage (%) = (Book Inventory − Physical Inventory) / Net Sales × 100",
    formulaPlain:
      "After a physical inventory count, take what your accounting records say you should have, subtract what you actually counted, then divide by net sales for the period. Multiply by 100. The result is the percent of sales you lost to shrink.",
    why:
      "Shrinkage flows directly into COGS and crushes gross margin one-for-one — every dollar of shrink is a dollar that doesn't reach the bottom line. The NRF's 2023 National Retail Security Survey put the US retail industry-wide average at approximately 1.6% of sales. Categories with high theft exposure (e.g. health & beauty, electronics) typically run higher than this average.",
    benchmarks: [
      { label: "Industry average (NRF 2023)", value: "~1.6% of sales", tone: "neutral" },
      { label: "Tight operation", value: "< 1.0% of sales", tone: "good" },
      { label: "Watch", value: "1.0–2.0% of sales", tone: "warn" },
      { label: "Material problem", value: "> 2.0% of sales", tone: "bad" },
    ],
    industryKey: "ecommerce",
    related: ["inventory-turnover", "gross-margin", "food-cost"],
  },
  {
    slug: "rule-of-40",
    title: "Rule of 40 (SaaS)",
    category: "Industry-Specific",
    short:
      "The Rule of 40 says a healthy SaaS company's revenue growth rate plus EBITDA margin should add up to at least 40.",
    formula: "Rule of 40 = Revenue Growth Rate (%) + EBITDA Margin (%)",
    formulaPlain:
      "Add your year-over-year revenue growth percentage to your EBITDA margin percentage. The sum should be ≥ 40.",
    why:
      "The Rule of 40 is the canonical SaaS efficiency benchmark — invented by Brad Feld and SaaStr. It captures the trade-off between growth and profitability. Either grow fast or be profitable; you don't need both, but you need their sum to clear 40.",
    benchmarks: [
      { label: "Best-in-class", value: "≥ 60", tone: "good" },
      { label: "Healthy", value: "40–60", tone: "good" },
      { label: "Underperforming", value: "25–40", tone: "warn" },
      { label: "Burning value", value: "< 25", tone: "bad" },
    ],
    industryKey: "saas",
    related: ["ebitda-margin", "gross-margin", "burn-multiple"],
  },
  {
    slug: "burn-multiple",
    title: "Burn Multiple (Startups)",
    category: "Industry-Specific",
    short:
      "Burn multiple measures how much cash a startup burns to generate $1 of new annual recurring revenue (ARR).",
    formula: "Burn Multiple = Net Cash Burned / Net New ARR",
    formulaPlain:
      "Take net cash burned in the period. Divide by net new annual recurring revenue added in the same period.",
    why:
      "Burn multiple is the cleanest startup efficiency metric. Coined by David Sacks. A burn multiple of 1× means $1 burned for every $1 of new ARR added. Below 1× is excellent. Above 3× and the business is probably not capital-efficient.",
    benchmarks: [
      { label: "Amazing", value: "< 1.0×", tone: "good" },
      { label: "Great", value: "1.0–1.5×", tone: "good" },
      { label: "OK", value: "1.5–2.0×", tone: "warn" },
      { label: "Suspect", value: "> 3.0×", tone: "bad" },
    ],
    industryKey: "saas",
    related: ["runway", "rule-of-40", "ltv-cac-ratio"],
  },
  {
    slug: "runway",
    title: "Runway (Startups)",
    category: "Industry-Specific",
    short:
      "Runway is how many months until your cash runs out at the current burn rate.",
    formula: "Runway (months) = Cash on Hand / Monthly Net Burn",
    formulaPlain:
      "Take your current cash balance. Divide by your average monthly net cash burn (operating outflows minus operating inflows).",
    why:
      "Runway is the single most important survival metric for any pre-profitable startup. The industry rule of thumb is to always have ≥ 18 months of runway — enough time to either become profitable, raise, or pivot before the lights go out.",
    benchmarks: [
      { label: "Comfortable", value: "≥ 24 months", tone: "good" },
      { label: "Healthy", value: "18–24 months", tone: "good" },
      { label: "Plan to raise now", value: "12–18 months", tone: "warn" },
      { label: "Danger zone", value: "< 12 months", tone: "bad" },
    ],
    industryKey: "saas",
    related: ["burn-multiple", "rule-of-40", "payback-period"],
  },

  // ---------- GROWTH / UNIT ECONOMICS ----------
  {
    slug: "customer-acquisition-cost",
    title: "Customer Acquisition Cost (CAC)",
    category: "Industry-Specific",
    short:
      "Customer Acquisition Cost (CAC) is the fully-loaded cost to acquire one new paying customer — sales + marketing spend divided by new customers won in the same period.",
    formula: "CAC = (Sales Spend + Marketing Spend) / New Customers Acquired",
    formulaPlain:
      "Add up everything you spent on sales and marketing in the period. Divide by the number of NEW paying customers you brought on. The result is what each customer cost you to land.",
    why:
      "CAC only makes sense when paired with LTV (Lifetime Value). A $200 CAC is cheap if customers stay for 5 years and pay $100/month; it is a problem if they churn in 6 weeks. Investors and acquirers use CAC + payback period to gauge whether growth spend is creating durable value or burning cash.",
    benchmarks: [
      { label: "Standalone metric", value: "Not meaningful alone — compare to LTV", tone: "neutral" },
      { label: "Conventional SaaS guideline", value: "LTV:CAC ≥ 3×", tone: "good" },
      { label: "Conventional SaaS guideline", value: "Payback < 12 months", tone: "good" },
      { label: "Industry variation", value: "Varies hugely by channel + sector", tone: "neutral" },
    ],
    example: {
      inputs: [
        { k: "Sales + marketing spend (quarter)", v: "$120,000" },
        { k: "New paying customers", v: "240" },
      ],
      result: "CAC = $120,000 ÷ 240 = $500 per customer",
    },
    industryKey: "saas",
    related: ["customer-lifetime-value", "ltv-cac-ratio", "payback-period"],
  },
  {
    slug: "customer-lifetime-value",
    title: "Customer Lifetime Value (LTV)",
    category: "Industry-Specific",
    short:
      "Customer Lifetime Value (LTV) is the total gross profit you expect to earn from a customer across their entire relationship with you.",
    formula: "LTV = Avg Revenue per Customer × Gross Margin × Customer Lifespan",
    formulaPlain:
      "Take your average revenue per customer per year, multiply by your gross margin %, then multiply by how many years (or months) a typical customer stays. That's the lifetime profit they generate.",
    why:
      "LTV tells you how much you can afford to spend acquiring a customer. The benchmark for SaaS is LTV ≥ 3× CAC and CAC payback under 12 months. Below those thresholds and you're funding growth with venture dollars, not customer profits — durability suffers.",
    benchmarks: [
      { label: "SaaS — strong", value: "LTV ≥ 3× CAC", tone: "good" },
      { label: "SaaS — workable", value: "LTV 2–3× CAC", tone: "warn" },
      { label: "SaaS — bleeding cash", value: "LTV < 1.5× CAC", tone: "bad" },
      { label: "E-commerce — strong", value: "LTV ≥ 4× CAC", tone: "good" },
    ],
    example: {
      inputs: [
        { k: "Avg revenue per customer per year", v: "$1,200" },
        { k: "Gross margin", v: "80%" },
        { k: "Avg lifespan", v: "4 years" },
      ],
      result: "LTV = $1,200 × 0.80 × 4 = $3,840 per customer",
    },
    industryKey: "saas",
    related: ["customer-acquisition-cost", "ltv-cac-ratio", "gross-margin"],
  },
  {
    slug: "ltv-cac-ratio",
    title: "LTV:CAC Ratio",
    category: "Industry-Specific",
    short:
      "LTV:CAC ratio compares the lifetime profit of a customer to the cost of acquiring them. The widely-quoted benchmark is 3:1 — three dollars of customer profit for every one dollar spent on acquisition.",
    formula: "LTV:CAC = Customer Lifetime Value / Customer Acquisition Cost",
    formulaPlain:
      "Divide LTV (gross profit you expect per customer) by CAC (what you spent to land that customer). A ratio of 3.0× means each customer generates three times what you spent winning them.",
    why:
      "LTV:CAC is the cleanest one-number test of whether a growth engine is sustainable. Below 1.0× you're paying customers to use you. Around 3.0× is healthy. Above 5.0× and you may be under-investing in growth — leaving market share for competitors to take.",
    benchmarks: [
      { label: "Under-investing in growth", value: "> 5.0×", tone: "warn" },
      { label: "Healthy", value: "3.0–5.0×", tone: "good" },
      { label: "Workable", value: "2.0–3.0×", tone: "warn" },
      { label: "Burning capital", value: "< 1.5×", tone: "bad" },
    ],
    industryKey: "saas",
    related: ["customer-acquisition-cost", "customer-lifetime-value", "payback-period"],
  },
  {
    slug: "monthly-recurring-revenue",
    title: "Monthly Recurring Revenue (MRR)",
    category: "Industry-Specific",
    short:
      "Monthly Recurring Revenue (MRR) is the normalized monthly subscription revenue a business expects, excluding one-time fees, setup charges, and overages.",
    formula: "MRR = Σ (Active Subscriptions × Monthly Price)",
    formulaPlain:
      "Take every active subscription, normalize annual plans to a monthly equivalent (annual ÷ 12), and sum them up. The result is the recurring revenue you'd collect this month if nothing changed.",
    why:
      "MRR is the canonical SaaS metric. It strips out lumpy one-time revenue so investors can see the underlying compounding base. ARR (annual recurring revenue) is just MRR × 12. Growth in MRR — especially Net New MRR after churn — is how SaaS multiples are set.",
    benchmarks: [
      { label: "Early growth", value: "10–20% MoM", tone: "good" },
      { label: "Steady scale", value: "3–8% MoM", tone: "good" },
      { label: "Mature", value: "1–3% MoM", tone: "neutral" },
      { label: "Stalled", value: "≤ 0% MoM", tone: "bad" },
    ],
    example: {
      inputs: [
        { k: "Monthly subscribers (× $79)", v: "320 × $79 = $25,280" },
        { k: "Annual subscribers (× $720)", v: "85 × ($720 ÷ 12) = $5,100" },
      ],
      result: "MRR = $25,280 + $5,100 = $30,380",
    },
    industryKey: "saas",
    related: ["net-revenue-retention", "rule-of-40", "burn-multiple"],
  },
  {
    slug: "net-revenue-retention",
    title: "Net Revenue Retention (NRR)",
    category: "Industry-Specific",
    short:
      "Net Revenue Retention (NRR) measures how much of last year's revenue from existing customers you kept — including expansion, downgrades, and churn — without counting any new customers won.",
    formula: "NRR = (Starting MRR + Expansion − Downgrade − Churn) / Starting MRR",
    formulaPlain:
      "Take the recurring revenue you started the period with. Add any expansion (upgrades, more seats), subtract downgrades and churn. Divide by the starting revenue.",
    why:
      "NRR above 100% means your existing book of customers grows on its own — without new sales spend. It is widely considered the single most predictive metric for SaaS company quality, because expansion revenue compounds at zero acquisition cost. Sub-90% NRR creates a leaky bucket that acquisition spending alone cannot outrun for long.",
    benchmarks: [
      { label: "Best-in-class", value: "> 120%", tone: "good" },
      { label: "Healthy", value: "100–120%", tone: "good" },
      { label: "Watch", value: "90–100%", tone: "warn" },
      { label: "Leaky bucket", value: "< 90%", tone: "bad" },
    ],
    industryKey: "saas",
    related: ["gross-revenue-retention", "monthly-recurring-revenue", "customer-lifetime-value"],
  },
  {
    slug: "gross-revenue-retention",
    title: "Gross Revenue Retention (GRR)",
    category: "Industry-Specific",
    short:
      "Gross Revenue Retention (GRR) measures the percentage of recurring revenue you retained from existing customers — counting only churn and downgrades. Unlike NRR, it excludes expansion, so it is capped at 100% and shows pure customer-base durability.",
    formula: "GRR = (Starting MRR − Downgrade − Churn) / Starting MRR",
    formulaPlain:
      "Take the recurring revenue you started the period with. Subtract downgrades and churn only — do NOT add expansion. Divide by the starting revenue. Multiply by 100. The result is the percent of customer revenue you retained on a like-for-like basis.",
    why:
      "GRR strips out the expansion makeup that can hide weak underlying retention in the NRR number. A SaaS company with 130% NRR and 80% GRR is leaning heavily on a small cohort of fast-expanding customers — risky concentration. The same NRR with 95% GRR signals broad-based durability. Institutional investors look at both side-by-side; the gap between them tells you how dependent growth is on a few accounts.",
    benchmarks: [
      { label: "Best-in-class", value: "> 95%", tone: "good" },
      { label: "Healthy", value: "90–95%", tone: "good" },
      { label: "Watch", value: "85–90%", tone: "warn" },
      { label: "Concerning", value: "< 85%", tone: "bad" },
    ],
    industryKey: "saas",
    related: ["net-revenue-retention", "monthly-recurring-revenue", "customer-lifetime-value"],
  },
  {
    slug: "payback-period",
    title: "CAC Payback Period",
    category: "Industry-Specific",
    short:
      "CAC Payback Period is the number of months a new customer's gross margin must cover before you recoup what you spent acquiring them.",
    formula: "Payback Months = CAC / (ARPU × Gross Margin / 12)",
    formulaPlain:
      "Divide your CAC by the monthly gross profit a customer generates. The result is how many months it takes to earn back the acquisition cost.",
    why:
      "Payback period answers the cash-flow question that LTV:CAC ignores: how long do I have to fund this customer before they break even? Faster payback means a more capital-efficient growth engine. Sub-12-month payback is the institutional SaaS standard; sub-24 is acceptable for higher-LTV enterprise sales.",
    benchmarks: [
      { label: "Best-in-class", value: "< 12 months", tone: "good" },
      { label: "Acceptable", value: "12–24 months", tone: "warn" },
      { label: "Capital-heavy", value: "24–36 months", tone: "warn" },
      { label: "Likely unprofitable", value: "> 36 months", tone: "bad" },
    ],
    example: {
      inputs: [
        { k: "CAC", v: "$1,200" },
        { k: "Monthly ARPU", v: "$200" },
        { k: "Gross margin", v: "75%" },
      ],
      result: "Payback = $1,200 ÷ ($200 × 0.75) = 8 months",
    },
    industryKey: "saas",
    related: ["customer-acquisition-cost", "ltv-cac-ratio", "burn-multiple"],
  },
  {
    slug: "average-order-value",
    title: "Average Order Value (AOV)",
    category: "Industry-Specific",
    short:
      "Average Order Value (AOV) is total revenue divided by the number of orders in a period. It is the single biggest lever for e-commerce profitability after gross margin.",
    formula: "AOV = Total Revenue / Number of Orders",
    formulaPlain:
      "Take revenue in the period, divide by the number of orders. The result is what a typical order is worth.",
    why:
      "AOV beats traffic for e-commerce profitability because every order has fixed costs (payment processing, fulfillment, customer service). Raising AOV via cross-sells, bundles, or free-shipping thresholds typically lifts contribution margin more than the equivalent traffic increase because no additional acquisition cost is incurred.",
    benchmarks: [
      { label: "Highly variable by category", value: "Benchmark against your sector, not gross averages", tone: "neutral" },
      { label: "Track trend, not absolute", value: "Direction of change matters more than the number", tone: "neutral" },
    ],
    industryKey: "ecommerce",
    related: ["gross-margin", "customer-lifetime-value", "inventory-turnover"],
  },
  {
    slug: "same-store-sales",
    title: "Same-Store Sales Growth",
    category: "Industry-Specific",
    short:
      "Same-Store Sales growth (also called comparable sales or comps) measures revenue change at locations open for at least a year — isolating organic growth from new-store openings.",
    formula: "Same-Store Sales % = (Current Period Sales − Prior Period Sales) / Prior Period Sales",
    formulaPlain:
      "Only count locations open for both periods. Compare current period revenue at those locations to the same period last year. The percent change is your comp.",
    why:
      "Same-store sales is the cleanest read on whether a retail or restaurant brand is healthy. Total revenue can grow purely from new openings while underlying stores shrink — a classic late-stage warning sign. Public retailers report comps quarterly because investors weight them more heavily than total revenue growth.",
    benchmarks: [
      { label: "Best-in-class growth", value: "> 8%", tone: "good" },
      { label: "Healthy", value: "3–8%", tone: "good" },
      { label: "Inflation-tracking", value: "0–3%", tone: "warn" },
      { label: "Erosion", value: "< 0%", tone: "bad" },
    ],
    industryKey: "restaurant",
    related: ["prime-cost", "food-cost", "labor-cost"],
  },

  // ---------- METHODOLOGY ----------
  {
    slug: "letter-grade",
    title: "How CFO Grade Assigns a Letter Grade",
    category: "Methodology",
    short:
      "Every CFO Grade report assigns a letter grade from A+ to F based on a composite score across 24+ financial ratios, weighted by industry.",
    formula:
      "Composite Score = Σ (metric_score × category_weight × industry_modifier)",
    formulaPlain:
      "Each of 24+ ratios gets a score from 0–100 based on how it compares to the industry-specific benchmark. Scores are grouped into four categories (profitability, liquidity, leverage, efficiency) and weighted. The composite maps to a letter grade.",
    why:
      "The letter grade exists so you can read a 3-second summary instead of 24 numbers. A+ to A- means the business would pass institutional credit review. B grades are healthy but with one or two flags. C grades have material issues. D and F are distress.",
    benchmarks: [
      { label: "A+ to A−", value: "Institutional quality", tone: "good" },
      { label: "B+ to B−", value: "Healthy with watch items", tone: "good" },
      { label: "C+ to C−", value: "Material issues to address", tone: "warn" },
      { label: "D / F", value: "Distress — restructure", tone: "bad" },
    ],
    related: ["ebitda-margin", "dscr", "current-ratio", "cash-conversion-cycle"],
  },
];

export function getConcept(slug) {
  if (!slug) return null;
  return LEARN_CONCEPTS.find((c) => c.slug === String(slug).toLowerCase()) || null;
}

export function getRelated(slug) {
  const c = getConcept(slug);
  if (!c) return [];
  return (c.related || []).map(getConcept).filter(Boolean);
}

export const LEARN_SLUGS = LEARN_CONCEPTS.map((c) => c.slug);

// ----------------------------------------------------------------------------
// AEO/GEO enrichment: hand-authored answer-first openers, quotable stats, and
// People-Also-Ask Q&As for the highest-traffic concepts. Concepts not listed
// here fall back to auto-derived defaults from /lib/seo.js (derivePaaForConcept)
// so every glossary page is AI-search ready.
// ----------------------------------------------------------------------------

export const CONCEPT_ENRICHMENT = {
  dscr: {
    answerFirst:
      "DSCR (Debt Service Coverage Ratio) is your operating income divided by total annual debt payments. A DSCR of 1.25× or higher is the threshold most lenders use to call a business 'bankable' — below 1.0× means you're not earning enough to cover debt service.",
    quickStat:
      "The median DSCR threshold for SBA 7(a) loan approval is 1.15× — but most banks privately underwrite to 1.25× to leave a safety margin.",
    paa: [
      { q: "What is DSCR?", a: "DSCR (Debt Service Coverage Ratio) measures how much operating cash flow a business generates relative to its annual debt payments. Calculate it as: EBITDA ÷ (Principal + Interest). A DSCR above 1.0 means the business earns enough to cover debt service." },
      { q: "What is a good DSCR?", a: "Most commercial lenders require a minimum DSCR of 1.25×. The SBA accepts 1.15× for 7(a) loans. Construction sureties typically want 1.5× or higher. A DSCR below 1.0 means the business is not covering its debt obligations from operations." },
      { q: "How do I calculate DSCR?", a: "DSCR = EBITDA ÷ (Annual Principal + Annual Interest). For a stricter version, use operating cash flow in the numerator and add lease payments to the denominator. CFO Grade computes both versions automatically from your P&L." },
      { q: "Is a 1.5 DSCR good?", a: "Yes. A DSCR of 1.5× means you generate $1.50 of operating income for every $1.00 of debt service — comfortably above the 1.25× threshold most banks require. Construction sureties and SBA underwriters often use 1.5× as a 'strong' benchmark." },
      { q: "How can I improve my DSCR fast?", a: "Three levers, fastest to slowest: (1) refinance to extend amortization (lowers the annual debt payment, instantly lifts DSCR), (2) raise gross margin via pricing or COGS cuts, (3) pay down high-interest balances. Avoid taking on additional debt — that compounds the problem." },
    ],
  },
  "quick-ratio": {
    answerFirst:
      "Quick Ratio (also called the Acid-Test) is cash + receivables + marketable securities divided by current liabilities. Unlike Current Ratio, it excludes inventory — so it answers a stricter question: could you pay every short-term obligation today without selling product?",
    quickStat:
      "Banks routinely covenant Quick Ratio ≥ 1.0× for commercial credit lines — falling below can trigger a technical default even if revenue is growing.",
    paa: [
      { q: "What is the quick ratio?", a: "Quick Ratio = (Cash + Receivables + Marketable Securities) ÷ Current Liabilities. It measures whether a business can pay every short-term obligation immediately, without selling inventory." },
      { q: "What is a good quick ratio?", a: "1.0× or higher is the institutional benchmark — it means liquid assets fully cover current liabilities. Capital-intensive businesses (manufacturing, distribution) often run below 1.0 because inventory is the biggest current asset. Service businesses should clear 1.0× comfortably." },
      { q: "Quick ratio vs current ratio — what's the difference?", a: "Current Ratio includes inventory in current assets. Quick Ratio excludes it. Quick Ratio is the stricter test because inventory may not convert to cash quickly — especially in retail, restaurants, or seasonal businesses." },
      { q: "Why do lenders use the quick ratio?", a: "Quick Ratio strips out inventory and prepaid expenses, leaving only assets that are truly liquid within 90 days. For working-capital revolvers, banks want to see real cash + AR available — not inventory that might sit unsold for months." },
    ],
  },
  "ebitda-margin": {
    answerFirst:
      "EBITDA Margin is EBITDA divided by Revenue, expressed as a percentage. It strips out financing, tax, and accounting decisions to show how much operational cash a business generates from each dollar of sales — a cleaner cross-industry profitability measure than net margin.",
  },
  "operating-margin": {
    answerFirst:
      "Operating Margin is Operating Income divided by Revenue. It captures profitability from core operations only — before interest expense (capital structure) and taxes (jurisdiction). It is a clean measure of operational efficiency that allows cross-business comparison.",
  },
  "debt-to-ebitda": {
    answerFirst:
      "Debt-to-EBITDA tells you how many years of operating cash earnings it would take to fully repay all debt. Below 3× is conventionally considered the 'healthy borrower' band that most banks underwrite to; above 5× is conventionally considered highly leveraged.",
  },
  dso: {
    answerFirst:
      "Days Sales Outstanding (DSO) measures the average number of days it takes to collect cash after invoicing. It is the single best diagnostic for whether a growing business is converting revenue into actual cash — or just papering the AR aging report.",
    quickStat:
      "Each 10 days of DSO improvement frees roughly 2.7% of annual revenue in working capital (10 ÷ 365). For a $5M-revenue business, cutting DSO from 70 to 40 days unlocks ~$410k of cash that was previously tied up in receivables.",
  },
  runway: {
    answerFirst:
      "Runway is how many months a business can operate at current burn before cash runs out. For pre-profitable startups, runway is the survival metric — the widely-cited rule of thumb is to keep it above 18 months so fundraising, hiring, and product decisions aren't compromised by short-term cash pressure.",
  },
  "rule-of-40": {
    answerFirst:
      "Rule of 40 is a SaaS heuristic popularised by Brad Feld and SaaStr: ARR growth rate (%) + EBITDA margin (%) should sum to at least 40. It captures the trade-off between growth and profitability — companies scoring 40+ are conventionally considered investable at SaaS multiples regardless of which side of the equation provides the points.",
  },
  "customer-acquisition-cost": {
    answerFirst:
      "Customer Acquisition Cost (CAC) is the fully-loaded sales + marketing spend per new customer landed. It is only meaningful when paired with LTV — a $500 CAC can be excellent or terrible depending on how long the customer stays and what they're worth.",
    quickStat:
      "A widely-cited institutional benchmark for SaaS is CAC payback within 12 months. Beyond 24 months, the growth engine effectively requires external capital to fund — useful directionally, but the specific cutoff varies by capital market conditions.",
  },
  "customer-lifetime-value": {
    answerFirst:
      "Customer Lifetime Value (LTV) is the total gross profit a customer generates across their relationship with you. It sets the ceiling on what you can rationally spend acquiring them. The conventional institutional benchmark is LTV ≥ 3× CAC.",
    quickStat:
      "LTV is mathematically very sensitive to churn — reducing monthly churn from 5% to 3% nearly doubles LTV, because average customer lifespan (1 ÷ monthly churn) rises from 20 months to 33 months at the same revenue level.",
  },
  "ltv-cac-ratio": {
    answerFirst:
      "LTV:CAC ratio is the conventional one-number test of a growth engine's durability. The 3:1 benchmark comes from SaaS investor heuristics (David Skok et al.): below it growth typically requires external capital, above ~5:1 you may be under-investing in acquisition.",
  },
  "monthly-recurring-revenue": {
    answerFirst:
      "Monthly Recurring Revenue (MRR) is the canonical SaaS metric — predictable subscription revenue normalised to a monthly cadence, with one-time fees and overages excluded. ARR is just MRR × 12.",
    quickStat:
      "Net New MRR (new + expansion − churn − contraction) is the directional metric institutional SaaS investors weight most heavily, because it strips out gross-add vanity from sustainable growth.",
  },
  "net-revenue-retention": {
    answerFirst:
      "Net Revenue Retention (NRR) above 100% means your existing book of customers grows on its own — expansion outpaces churn and downgrades. It is widely considered the single most predictive metric of SaaS company quality.",
  },
  "payback-period": {
    answerFirst:
      "CAC Payback Period is the number of months a new customer's gross margin takes to repay the acquisition cost. It answers the cash-flow question LTV:CAC ignores: how long do you have to fund this customer before they break even? Sub-12-month payback is the conventional SaaS benchmark.",
  },
  "average-order-value": {
    answerFirst:
      "Average Order Value (AOV) is total revenue divided by number of orders. It is mathematically a high-leverage e-commerce metric after gross margin, because every order has fixed costs (payment processing, packing, fulfillment) — raising AOV lifts contribution margin without proportionally lifting those costs.",
  },
  "same-store-sales": {
    answerFirst:
      "Same-Store Sales growth (also called comparable sales or comps) measures revenue change at locations open for at least a year. Investors and analysts weight comps more heavily than total revenue growth because total revenue can grow purely from new-store openings while underlying stores shrink.",
  },
  ebitda: {
    answerFirst:
      "EBITDA stands for Earnings Before Interest, Taxes, Depreciation, and Amortization. It is a proxy for operational cash earnings before financing and tax structure decisions. Lenders, acquirers, and PE firms use it as a primary measure of a business's ongoing earning power.",
    paa: [
      { q: "What is EBITDA?", a: "EBITDA = Earnings Before Interest, Taxes, Depreciation, and Amortization. It strips out financing and accounting decisions to show how much operational cash a business generates. It is the most common multiple used in M&A and lending." },
      { q: "How is EBITDA calculated?", a: "Start with net income, then add back: interest expense, tax expense, depreciation, and amortization. Alternatively, start with operating income and add back depreciation and amortization." },
      { q: "What is a good EBITDA margin?", a: "EBITDA margin (EBITDA ÷ Revenue) varies widely by industry — software businesses typically post higher margins than distribution or retail. Industry context matters more than the absolute number; benchmark against your sector." },
      { q: "Why is EBITDA important?", a: "EBITDA isolates operating performance from capital structure (interest) and tax jurisdiction (taxes), so two businesses can be compared apples-to-apples. It also approximates the cash a buyer or lender would have available to service debt." },
    ],
  },
  "gross-margin": {
    answerFirst:
      "Gross margin is the percentage of revenue left after subtracting direct costs (COGS). It is the ceiling on every downstream margin — if a business can't profit at the unit level, no amount of scale will fix it.",
    paa: [
      { q: "What is gross margin?", a: "Gross margin is (Revenue − COGS) ÷ Revenue, expressed as a percentage. It measures how much money is left from each dollar of sales after the direct cost of producing the product or delivering the service." },
      { q: "How do I calculate gross margin?", a: "Gross margin (%) = (Revenue − COGS) ÷ Revenue × 100. Example: $1M revenue, $620k COGS = $380k gross profit = 38% gross margin." },
      { q: "What is a good gross margin?", a: "It depends on the industry. Software is conventionally high (70–85%); professional services mid-range (40–60%); retail and e-commerce lower (30–50%); distribution and construction lower still (15–25%). CFO Grade benchmarks your margin against your sector automatically." },
      { q: "What's the difference between gross margin and net margin?", a: "Gross margin only subtracts direct costs (COGS). Net margin subtracts everything — operating expenses, interest, taxes. A 40% gross margin business with high overhead might still net only 5%." },
    ],
  },
  "current-ratio": {
    answerFirst:
      "Current ratio is current assets divided by current liabilities. It tells you whether a business can pay its bills over the next 12 months. A current ratio of 1.5× or higher is conventionally considered comfortable; below 1.0× means current liabilities exceed liquid assets.",
    paa: [
      { q: "What is the current ratio?", a: "Current ratio = Current Assets ÷ Current Liabilities. It measures whether a business has enough liquid assets to cover obligations due within 12 months." },
      { q: "What is a good current ratio?", a: "1.5× to 2.0× is conventionally considered healthy. Below 1.0× signals potential liquidity stress. A persistently high ratio (e.g. above 2.5×) can indicate idle capital — too much cash or inventory sitting still." },
      { q: "How is current ratio different from quick ratio?", a: "Quick ratio excludes inventory from current assets, so it is a stricter test of immediate liquidity. Quick ratio = (Cash + AR + Marketable Securities) ÷ Current Liabilities." },
      { q: "Why does current ratio matter to lenders?", a: "Lenders use it as a covenant — many credit agreements require maintaining a current ratio above 1.25× or 1.5×. Falling below the threshold can trigger default, even if you are paying on time." },
    ],
  },
  "net-margin": {
    answerFirst:
      "Net margin is net income divided by revenue, expressed as a percentage. It is the bottom-line profitability of a business after all costs — COGS, operating expenses, interest, and taxes.",
  },
  "working-capital": {
    answerFirst:
      "Working capital is current assets minus current liabilities — the cash and near-cash you have available to fund day-to-day operations. Positive working capital means a business can absorb short-term shocks; negative working capital means it depends on continuous sales and credit to stay solvent.",
    quickStat:
      "Negative working capital is not always a problem. Some large retailers and subscription businesses run on it sustainably because they collect cash from customers before paying suppliers. It becomes dangerous when paired with shrinking sales or rising AR days.",
  },
};

// Returns the enrichment block for a concept, or null if none authored.
export function getConceptEnrichment(slug) {
  if (!slug) return null;
  return CONCEPT_ENRICHMENT[String(slug).toLowerCase()] || null;
}
